import { z } from 'zod';
import { LIVE_SHARING_PILLARS, SHARING_PILLARS, type SharingPillar, type SharingPillarId } from './pillars.js';

/**
 * The SHARING FLOOR a sponsored engagement carries (steward-app#1023, SCS 3.1; founder
 * decisions on steward-app#1016, "Conversion re-scoped" and "Second round", 2026-09-16).
 *
 * While a family workspace has an ACTIVE `sponsored` engagement with a firm, the family cannot
 * turn off the sharing areas that firm's advisors see. What those areas are is recorded ONCE,
 * at setup, on the engagement's `floorPolicy` string (appcore `entities/engagement.ts`:
 * `z.string().max(200)`, opaque to appcore, "enforced by the consuming app"). This module is
 * the ONE definition of that string's format: the writer (SCS 3.2) builds it with
 * {@link buildSponsoredFloorPolicy}, and the façade's enforcement
 * (`services/steward-app-api/src/services/sponsoredFloor.ts`) reads it back with
 * {@link sponsoredFloorAreas}.
 *
 * ⚠️ **A SNAPSHOT, never a live setting.** The family consented to what was disclosed when
 * they accepted. So enforcement reads the engagement's own copy, and nothing an operator or a
 * firm changes later can widen an existing family's floor.
 *
 * ⚠️ **No per-firm setting in v1** (second round, decision 2). Every v1 policy records the
 * full floor, {@link FULL_SPONSORED_FLOOR_AREAS}. Since steward-app#1971 the façade's two
 * provision sites write a v2 policy instead (see the version 2 section below): its `areas` are
 * the areas the firm's own setting locked when the setup was made, possibly none.
 *
 * ⚠️ **Credentials are not representable.** The vault is not a sharing area
 * (`SharingPillarId`), and the parser refuses any id outside `SHARING_PILLARS`.
 *
 * ⚠️ **Folders marked "Only your family" are NOT part of the floor** (second round, decision
 * 1). The floor is the AREAS (the pillar roles on the advisor's membership); a family may still
 * keep an individual Documents folder private, and the façade deliberately does not guard the
 * folder ACL or visibility routes.
 */

/** appcore's bound on `Engagement.floorPolicy` (`z.string().max(200)`). */
export const SPONSORED_FLOOR_POLICY_MAX_LENGTH = 200;

/** The format version this module writes, and the only one {@link parseSponsoredFloorPolicy}
 *  and {@link sponsoredFloorAreas} read. Version 2 is read by {@link recordedSponsoredFloor} alone. */
export const SPONSORED_FLOOR_POLICY_VERSION = 1;

/**
 * The version of the acceptance disclosure copy a v1 sponsorship shows. SCS 3.3 renders that
 * copy; bump this string whenever the copy changes, so the engagement records which words the
 * family actually accepted.
 *
 *   - `sponsored-accept-v1` (second round, decision 1, 2026-09-16): "While {Firm} sponsors this
 *     workspace, they can see everything in it, including any Social Security numbers you add,
 *     except credentials and any folder you mark as only your family."
 *   - `sponsored-accept-v2` (Matt, 2026-09-22 QA, steward-app#1714, replacing v1): "{Firm} will
 *     be able to see your workspace details, except for any private document folders you create
 *     and your credentials." No SSN mention. This is the copy this constant points at, and
 *     what a v1-policy sponsorship is still shown; see `src/workspace/sponsoredDisclosure.ts`'s
 *     `DISCLOSURE_COPY` for both keys, kept side by side rather than edited in place.
 *
 * A v2-policy sponsorship is shown {@link SPONSORED_ACCEPT_DISCLOSURE_VERSION_V3} instead.
 */
export const SPONSORED_ACCEPT_DISCLOSURE_VERSION = 'sponsored-accept-v2';

/**
 * Every sharing area, in table order: the v1 floor, and what an absent or unreadable policy is
 * enforced as (fail closed).
 *
 * TOTAL over `SHARING_PILLARS`, not narrowed to the live ones: a floor that REFUSES removal of
 * a granted-but-not-yet-armed pillar role refuses something harmless, while a floor that
 * silently omitted one would stop protecting it the day it arms. Identical sets today
 * (`PILLAR_GRANTED_NOT_YET_ARMED` is empty as of 2026-09-14).
 */
export const FULL_SPONSORED_FLOOR_AREAS: readonly SharingPillarId[] = SHARING_PILLARS.map(p => p.id);

/** The parsed shape. */
export interface SponsoredFloorPolicy {
  readonly v: typeof SPONSORED_FLOOR_POLICY_VERSION;
  /** Distinct, non-empty, in `SHARING_PILLARS` order. */
  readonly areas: readonly SharingPillarId[];
  readonly disclosure: string;
}

const PILLAR_IDS: ReadonlySet<string> = new Set(SHARING_PILLARS.map(p => p.id));

/**
 * Strict at every level: an unknown key, an unknown area, a duplicate, an empty list or a
 * version other than 1 is a REJECTION, which enforcement then treats as the full floor.
 *
 * ⚠️ **An empty `areas` list is refused rather than read as "no floor".** A sponsorship with
 * nothing floored is not a v1 product state, and a writer bug that produced `[]` must not
 * quietly unlock a sponsored family. If "sponsored, nothing floored" is ever wanted, it is a
 * new version with its own reviewed meaning.
 */
const SponsoredFloorPolicySchema = z.strictObject({
  v: z.literal(SPONSORED_FLOOR_POLICY_VERSION),
  areas: z.array(z.string().refine(id => PILLAR_IDS.has(id), 'unknown sharing area'))
    .min(1)
    .refine(ids => new Set(ids).size === ids.length, 'duplicate sharing area'),
  disclosure: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
});

/** Table order, so one set of areas always serialises to the same bytes. */
function inTableOrder(ids: readonly string[]): SharingPillarId[] {
  const chosen = new Set(ids);
  return SHARING_PILLARS.filter(p => chosen.has(p.id)).map(p => p.id);
}

/**
 * The `floorPolicy` string for an engagement, e.g.
 * `{"v":1,"areas":["portfolio","documents","contacts","entities","people"],"disclosure":"sponsored-accept-v2"}`
 * (107 characters with all five areas and the current `SPONSORED_ACCEPT_DISCLOSURE_VERSION`,
 * pinned in `sponsoredFloor.test.ts` against the live constant, not this literal).
 *
 * Throws on anything {@link parseSponsoredFloorPolicy} would refuse, including a result longer
 * than appcore accepts, so a writer can never store a policy that enforcement reads as garbage.
 */
export function buildSponsoredFloorPolicy(input: {
  readonly areas: readonly SharingPillarId[];
  readonly disclosure: string;
}): string {
  const parsed = SponsoredFloorPolicySchema.parse({
    v: SPONSORED_FLOOR_POLICY_VERSION, areas: [...input.areas], disclosure: input.disclosure,
  });
  const raw = JSON.stringify({ v: parsed.v, areas: inTableOrder(parsed.areas), disclosure: parsed.disclosure });
  if (raw.length > SPONSORED_FLOOR_POLICY_MAX_LENGTH) {
    throw new Error(`floorPolicy is ${raw.length} characters; appcore accepts at most ${SPONSORED_FLOOR_POLICY_MAX_LENGTH}`);
  }
  return raw;
}

/** The parsed policy, or `null` for anything that is not exactly a v1 policy. Never throws. */
export function parseSponsoredFloorPolicy(raw: unknown): SponsoredFloorPolicy | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > SPONSORED_FLOOR_POLICY_MAX_LENGTH) return null;
  let json: unknown;
  try { json = JSON.parse(raw); } catch { return null; }
  const result = SponsoredFloorPolicySchema.safeParse(json);
  if (!result.success) return null;
  return { v: result.data.v, areas: inTableOrder(result.data.areas), disclosure: result.data.disclosure };
}

/**
 * The areas a sponsored engagement floors, and whether that answer came from the record.
 *
 * ⚠️ **FAIL CLOSED: `absent` and `unparseable` both enforce {@link FULL_SPONSORED_FLOOR_AREAS}.**
 * A sponsored pair is only ever written by the firm's setup flow, so a missing or malformed
 * policy is a writer defect, and the family-safe reading of a defect in a LOCK is not "unlocked"
 * but "the lock the founder ruled every sponsorship carries".
 */
export function sponsoredFloorAreas(raw: string | undefined): {
  readonly areas: readonly SharingPillarId[];
  readonly source: 'recorded' | 'absent' | 'unparseable';
} {
  if (raw === undefined || raw === '') return { areas: FULL_SPONSORED_FLOOR_AREAS, source: 'absent' };
  const parsed = parseSponsoredFloorPolicy(raw);
  return parsed
    ? { areas: parsed.areas, source: 'recorded' }
    : { areas: FULL_SPONSORED_FLOOR_AREAS, source: 'unparseable' };
}

/**
 * The pillar ROLE ids a set of floored areas protects: each area's primary role plus its
 * `additionalRoleIds` (the people pillar's tax-id reveal role, which the founder put inside the
 * floor by name). The same `[roleId, ...additionalRoleIds]` derivation the façade's
 * `areaRoleIds` grants with, so a role the grant writes is a role the floor protects.
 */
export function sponsoredFloorRoleIds(areas: readonly SharingPillarId[]): string[] {
  const chosen = new Set(areas);
  return SHARING_PILLARS.filter(p => chosen.has(p.id)).flatMap(p => [p.roleId, ...p.additionalRoleIds]);
}

// ── Version 2 and the effective lock (steward-app#1971 step S2) ────────────────────────────
//
// The v1 readers above do not read a v2 policy: {@link parseSponsoredFloorPolicy} refuses one
// and {@link sponsoredFloorAreas} reads it as unparseable. {@link buildSponsoredFloorPolicy}
// writes `v: 1`; {@link buildSponsoredFloorPolicyV2} writes `v: 2`.

/** The second `floorPolicy` format version. Differs from v1 in one way: `areas` may be empty. */
export const SPONSORED_FLOOR_POLICY_VERSION_2 = 2;

/**
 * The disclosure versions a v2 policy may name. A v2 policy naming any other is refused, and so
 * read as the full floor. (v1 accepts any well-formed disclosure string; that is unchanged.)
 */
export const SPONSORED_FLOOR_POLICY_V2_DISCLOSURES: readonly string[] = ['sponsored-accept-v3'];

/**
 * The acceptance disclosure a v2 sponsorship shows (steward-app#1971; wording approved by Matt
 * 2026-10-01, on the issue). Its sentences are built from two area lists (what the firm's
 * advisors start with, and what the sponsorship locks), so the version names the WORDING and
 * the lists ride beside it: `/me` projects both, and the accept route requires both back.
 *
 * {@link SPONSORED_ACCEPT_DISCLOSURE_VERSION} is NOT bumped to this: that constant stays the
 * copy a v1-policy sponsorship (one set up before per-firm locks) is shown.
 */
export const SPONSORED_ACCEPT_DISCLOSURE_VERSION_V3 = 'sponsored-accept-v3';

/** The parsed v2 shape. `areas` is distinct, in `SHARING_PILLARS` order, and MAY BE EMPTY. */
export interface SponsoredFloorPolicyV2 {
  readonly v: typeof SPONSORED_FLOOR_POLICY_VERSION_2;
  readonly areas: readonly SharingPillarId[];
  readonly disclosure: string;
}

/**
 * Strict at every level, as the v1 schema is: an unknown key, an unknown area, a duplicate, a
 * disclosure outside {@link SPONSORED_FLOOR_POLICY_V2_DISCLOSURES} or a version other than 2
 * is a rejection. The one difference from v1 is that an empty `areas` list is ACCEPTED: in v2
 * it is the record of a sponsorship that floors nothing.
 */
const SponsoredFloorPolicyV2Schema = z.strictObject({
  v: z.literal(SPONSORED_FLOOR_POLICY_VERSION_2),
  areas: z.array(z.string().refine(id => PILLAR_IDS.has(id), 'unknown sharing area'))
    .refine(ids => new Set(ids).size === ids.length, 'duplicate sharing area'),
  disclosure: z.string().refine(d => SPONSORED_FLOOR_POLICY_V2_DISCLOSURES.includes(d), 'unknown v2 disclosure'),
});

/**
 * The parsed policy, or `null` for anything that is not exactly a v2 policy (a v1 policy
 * included). Never throws. The same outer checks as {@link parseSponsoredFloorPolicy}: a
 * non-empty string of at most {@link SPONSORED_FLOOR_POLICY_MAX_LENGTH} characters holding JSON.
 */
export function parseSponsoredFloorPolicyV2(raw: unknown): SponsoredFloorPolicyV2 | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > SPONSORED_FLOOR_POLICY_MAX_LENGTH) return null;
  let json: unknown;
  try { json = JSON.parse(raw); } catch { return null; }
  const result = SponsoredFloorPolicyV2Schema.safeParse(json);
  if (!result.success) return null;
  return { v: result.data.v, areas: inTableOrder(result.data.areas), disclosure: result.data.disclosure };
}

/**
 * The v2 `floorPolicy` string, e.g. `{"v":2,"areas":[],"disclosure":"sponsored-accept-v3"}`.
 * `areas` may be empty: the record of a sponsorship that locks nothing.
 *
 * Throws on anything {@link parseSponsoredFloorPolicyV2} would refuse, and on a result longer
 * than appcore accepts, as {@link buildSponsoredFloorPolicy} does for v1.
 */
export function buildSponsoredFloorPolicyV2(input: {
  readonly areas: readonly SharingPillarId[];
  readonly disclosure: string;
}): string {
  const parsed = SponsoredFloorPolicyV2Schema.parse({
    v: SPONSORED_FLOOR_POLICY_VERSION_2, areas: [...input.areas], disclosure: input.disclosure,
  });
  const raw = JSON.stringify({ v: parsed.v, areas: inTableOrder(parsed.areas), disclosure: parsed.disclosure });
  if (raw.length > SPONSORED_FLOOR_POLICY_MAX_LENGTH) {
    throw new Error(`floorPolicy is ${raw.length} characters; appcore accepts at most ${SPONSORED_FLOOR_POLICY_MAX_LENGTH}`);
  }
  return raw;
}

/**
 * The recorded floor R of an engagement, reading BOTH versions: {@link sponsoredFloorAreas}'
 * answer for everything that function already answers, plus a v2 policy read as its own
 * `areas` (possibly none).
 *
 * ⚠️ **FAIL CLOSED, as v1 is.** Absent, empty, over-length, not JSON, an unknown `v`, a v1
 * policy with an empty list, and a v2 policy this module refuses all give
 * {@link FULL_SPONSORED_FLOOR_AREAS}. The only input that gives fewer than all five is a
 * policy one of the two parsers accepts, and the only one that gives NONE is a v2 policy whose
 * `areas` is `[]`.
 */
export function recordedSponsoredFloor(raw: string | undefined): {
  readonly areas: readonly SharingPillarId[];
  readonly source: 'recorded' | 'absent' | 'unparseable';
} {
  const v2 = parseSponsoredFloorPolicyV2(raw);
  return v2 ? { areas: v2.areas, source: 'recorded' } : sponsoredFloorAreas(raw);
}

/**
 * A timestamp as epoch milliseconds, or `null`.
 *
 * Accepts `YYYY-MM-DDTHH:MM:SSZ` with zero to three fractional-second digits, and only when
 * the value ROUND-TRIPS: padded to three fractional digits, it must equal the `toISOString()`
 * of the instant `Date.parse` gives for it. So a string with no `Z`, an offset, a fourth
 * fractional digit, or a calendar value `Date.parse` would roll over (a 31st of February, an
 * hour 24) is `null`.
 *
 * The fraction is optional, rather than fixed at three digits as appcore's `since` pattern
 * is, because an engagement's `startedAt` is an unconstrained string in appcore's schema.
 * For the same reason two stamps are compared as numbers, never as strings.
 */
function utcInstant(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,3}))?Z$/.exec(value);
  if (!match) return null;
  const normalised = `${match[1]}.${(match[2] ?? '').padEnd(3, '0')}Z`;
  const ms = Date.parse(normalised);
  if (!Number.isFinite(ms) || new Date(ms).toISOString() !== normalised) return null;
  return ms;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * How the firm's setting bears on ONE area, for an engagement that started at `startedAt`:
 * whether the setting keeps the area in the lock (`true`) or takes it out (`false`).
 *
 * `setting` must already be known to be `state: 'set'` with an object `areas`.
 */
function settingKeeps(areas: Record<string, unknown>, areaId: string, startedAt: number | null): boolean {
  // Own keys only: `areas` is parsed JSON, and `constructor` must not read as a contained area.
  if (!Object.prototype.hasOwnProperty.call(areas, areaId)) return false;
  const entry = areas[areaId];
  const since = isPlainObject(entry) ? utcInstant(entry['since']) : null;
  // Fail closed: the setting contains the area, and either stamp cannot be read.
  if (since === null || startedAt === null) return true;
  return since <= startedAt;
}

/**
 * The areas a sponsorship LOCKS right now. PURE: no I/O, no clock.
 *
 * Area `a` is locked only if ALL of these hold:
 *   1. `a` is in the recorded floor ({@link recordedSponsoredFloor} of `floorPolicy`);
 *   2. the firm's setting is `set`, contains `a`, and `since[a] <= startedAt`;
 *   3. `a` is a live pillar;
 *   4. `heldRoleIds` contains `a`'s primary role id.
 *
 * **Every term can only remove an area from the recorded floor**: the result is a subset of
 * (recorded floor ∩ live pillars ∩ held), for every input. `sponsoredFloorV2.test.ts` checks
 * that over an enumerated table.
 *
 * `firmSponsorLock` is the firm's setting as appcore serves it on a client-side engagement
 * row: `{ state: 'unset' } | { state: 'set', areas: { [areaId]: { since } } }` (appcore
 * origin/main, `packages/core/src/db/advisor-firm-repository.ts` `FirmSponsorLockState` and
 * `packages/core/src/entities/advisor-firm.ts` `AdvisorFirmSponsorLockSchema`, read
 * 2026-10-01). appcore leaves the field OFF the row when it could not read or parse the
 * firm's record, so `undefined` here means "not known", not "unset".
 *
 * How condition 2 is decided when something is missing or malformed:
 *   - `firmSponsorLock` is `undefined`, not an object, has a `state` other than `'unset'` or
 *     `'set'`, or is `set` with `areas` that is not an object: condition 2 is SKIPPED, and
 *     conditions 1, 3 and 4 decide alone. Fail closed to the recorded floor.
 *   - `state: 'unset'`: nothing is locked.
 *   - `set`, and `areas` has no own key `a`: `a` is not locked.
 *   - `set`, contains `a`, and `since[a]` or `startedAt` is missing or not a timestamp
 *     `utcInstant` accepts: condition 2 is treated as met for `a`. Fail closed, for the areas the setting
 *     contains only; an area the setting does not contain stays unlocked.
 *   - `since[a]` equal to `startedAt` counts as locked. Both are compared as instants.
 *
 * `heldRoleIds` is the caller's: the role ids held today by the advisors the sponsorship
 * covers. Which advisors those are is not decided here. An area is held when its PRIMARY role
 * id is present; an additional or OFF role id alone does not make it held.
 *
 * `livePillars` is the table condition 3 is decided against, and the order of the result. It
 * defaults to {@link LIVE_SHARING_PILLARS}.
 */
export function effectiveLock(
  input: {
    readonly floorPolicy: string | undefined;
    readonly startedAt: string | undefined;
    readonly firmSponsorLock: unknown;
    readonly heldRoleIds: Iterable<string>;
  },
  livePillars: readonly SharingPillar[] = LIVE_SHARING_PILLARS,
): SharingPillarId[] {
  const recorded = new Set<string>(recordedSponsoredFloor(input.floorPolicy).areas);
  const held = new Set<string>(input.heldRoleIds);
  const setting = input.firmSponsorLock;

  let keeps: (areaId: string) => boolean = () => true;
  if (isPlainObject(setting)) {
    if (setting['state'] === 'unset') {
      keeps = () => false;
    } else if (setting['state'] === 'set' && isPlainObject(setting['areas'])) {
      const areas = setting['areas'];
      const startedAt = utcInstant(input.startedAt);
      keeps = areaId => settingKeeps(areas, areaId, startedAt);
    }
  }

  return livePillars
    .filter(p => recorded.has(p.id) && keeps(p.id) && held.has(p.roleId))
    .map(p => p.id);
}
